import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Space, theme, Button, Affix } from 'antd';
import { ArrowLeftOutlined, EditOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const { Title, Text } = Typography;

const PageWrapper = ({
  title,
  subtitle,
  icon,
  children,
  headerActions,
  headerLeftActions,
  showCard = true,
  cardProps = {},
  tabs,
  searchBar,
  className,
  style,
  isDetailsPage = false,
  listPagePath,
  onGoToList,
  entityName,
  onEdit,
  editLabel = 'Edit',
  backButtonLabel,
  helpContent,
  /** When set, rendered under the title unless `subtitle` is provided */
  totalCount,
  totalCountText = 'items',
}) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [helpPanelCollapsed, setHelpPanelCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!title) {
    console.warn('PageWrapper: title is required but was not provided');
  }

  const pageStyle = {
    padding: 24,
    background: token.colorBgLayout || token.colorBgContainer,
    minHeight: '100vh',
    ...style
  };

  const handleGoToList = () => {
    if (onGoToList) {
      onGoToList();
    } else if (listPagePath) {
      navigate(listPagePath);
    }
  };

  const displaySubtitle =
    subtitle ??
    (totalCount != null ? (
      <Text type="secondary" style={{ fontSize: 14 }}>
        ({totalCount} {totalCountText})
      </Text>
    ) : null);

  const header = (
    <Row justify="space-between" align="top" style={{ marginBottom: showCard ? 24 : 12 }}>
      <Col flex="auto">
        <div style={{ display: 'flex', alignItems: displaySubtitle ? 'flex-start' : 'center', gap: 16 }}>
          {icon && (
            <div style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: token.colorPrimaryBg || token.colorFillAlter || 'rgba(0, 94, 184, 0.1)',
              color: token.colorPrimary,
              flexShrink: 0,
              fontSize: 24,
              marginTop: 0
            }}>
              {icon}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {headerLeftActions && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {headerLeftActions}
                </div>
              )}
              <Title level={2} style={{ margin: 0, marginBottom: (displaySubtitle || (isDetailsPage && (listPagePath || onGoToList) && entityName)) ? 4 : 0, lineHeight: '32px' }}>
                {title}
              </Title>
              {onEdit && (
                <Button
                  icon={<EditOutlined />}
                  onClick={onEdit}
                  style={{ marginLeft: 8 }}
                >
                  {editLabel}
                </Button>
              )}
            </div>
            {(displaySubtitle || (isDetailsPage && (listPagePath || onGoToList) && (entityName || backButtonLabel))) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                {displaySubtitle && (
                  typeof displaySubtitle === 'string' ? (
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {displaySubtitle}
                    </Text>
                  ) : (
                    <div style={{ fontSize: 14 }}>
                      {displaySubtitle}
                    </div>
                  )
                )}
                {isDetailsPage && (listPagePath || onGoToList) && (entityName || backButtonLabel) && (
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleGoToList}
                    style={{
                      padding: 0,
                      height: 'auto',
                      fontSize: 14,
                    }}
                  >
                    {backButtonLabel || `Go to ${entityName}`}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Col>
      {headerActions && (
        <Col flex="none">
          <Space>{headerActions}</Space>
        </Col>
      )}
    </Row>
  );

  const defaultCardProps = {
    style: { borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column' },
    // overflow: hidden is only needed to make the two-pane helpContent layout scroll independently.
    // Without helpContent it clips content and breaks position: sticky (e.g. form save bars) for
    // no benefit, so only apply it when helpContent is actually rendered.
    bodyStyle: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: helpContent ? 'hidden' : 'visible' },
    ...cardProps
  };

  const content = (
    <div style={{ display: 'flex', gap: 24, position: 'relative', height: '100%', minHeight: '100%', flex: 1 }}>
      <div style={{ flex: helpContent && !helpPanelCollapsed ? '1 1 60%' : 1, minWidth: 0 }}>
        {tabs && (
          <div style={{ padding: '0 24px' }}>
            {tabs}
          </div>
        )}
        {searchBar && (
          <div style={{ 
            padding: '16px 24px', 
            borderBottom: `1px solid ${token.colorBorderSecondary}` 
          }}>
            {searchBar}
          </div>
        )}
        <div style={{ padding: showCard ? 24 : '0 24px 24px 24px' }}>
          {children}
        </div>
      </div>
      
      {helpContent && (
        <>
          {!helpPanelCollapsed && (
            <div style={{ 
              flex: '0 0 40%',
              maxWidth: '40%',
              height: '100%',
              minHeight: '100%',
              overflow: 'hidden',
              padding: '16px',
              borderLeft: `1px solid ${token.colorBorder}`,
              backgroundColor: token.colorFillQuaternary,
              borderRadius: token.borderRadius,
              transition: 'all 0.3s ease',
              display: isMobile ? 'none' : 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch'
            }}>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <Button
                  icon={<MenuFoldOutlined />}
                  onClick={() => setHelpPanelCollapsed(true)}
                  size="small"
                  type="text"
                />
              </div>
              <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', position: 'relative' }}>
                <Space direction="vertical" style={{ width: '100%', minHeight: '100%' }} size="small">
                  {React.Children.map(React.Children.toArray(helpContent), (child, index) => {
                    // Check if first child is Typography.Title by checking for 'level' prop
                    if (index === 0 && React.isValidElement(child) && child.props?.level !== undefined) {
                      return (
                        <Affix offsetTop={0} key="affix-title">
                          <div style={{ 
                            backgroundColor: token.colorFillQuaternary, 
                            padding: '8px 0', 
                            margin: '-8px 0 8px 0', 
                            zIndex: 10 
                          }}>
                            {child}
                          </div>
                        </Affix>
                      );
                    }
                    return child;
                  })}
                </Space>
              </div>
            </div>
          )}
          {helpPanelCollapsed && (
            <Button
              icon={<MenuUnfoldOutlined />}
              onClick={() => setHelpPanelCollapsed(false)}
              size="small"
              style={{
                position: 'sticky',
                top: 24,
                alignSelf: 'flex-start',
                marginLeft: '16px',
                marginRight: '16px',
                padding: '4px 12px',
                height: 'auto'
              }}
            >
              Show Help
            </Button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={className} style={pageStyle}>
      {header}
      {showCard ? (
        <Card {...defaultCardProps}>
          {content}
        </Card>
      ) : (
        content
      )}
    </div>
  );
};

PageWrapper.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
  headerActions: PropTypes.node,
  headerLeftActions: PropTypes.node,
  showCard: PropTypes.bool,
  cardProps: PropTypes.object,
  tabs: PropTypes.node,
  searchBar: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  isDetailsPage: PropTypes.bool,
  listPagePath: PropTypes.string,
  onGoToList: PropTypes.func,
  entityName: PropTypes.string,
  onEdit: PropTypes.func,
  editLabel: PropTypes.string,
  backButtonLabel: PropTypes.string,
  helpContent: PropTypes.node,
  totalCount: PropTypes.number,
  totalCountText: PropTypes.string,
};

export default PageWrapper;
